import { Stack, Text, Button } from '@instamenu/ui-system-next';
import { DatePicker } from '@instamenu/ui-system-next';
import { ModalNext } from '@instamenu/ui-system-next';
import {
  orderScheduleCalendar,
  orderScheduleModalContent,
  orderScheduleTimeSlotsScrollable,
  orderScheduleTimesSection,
} from './order-schedule.css';
import { orderScheduleModal } from './order-schedule.css';
import { DateTime } from 'luxon';
import { TimeZoneName } from './time-zone-name';
import { OrderTimeSlots } from './order-time-slots';
import { Matcher } from 'react-day-picker';
import { Order, useCheckoutContext } from '@instamenu/context-checkout';
import { themeVars } from '@instamenu/ui-vanilla-extract';
import { useCallback, useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';

const CloseButton = ({
  setOpen,
  scheduledAt,
}: {
  setOpen: (open: boolean) => void;
  scheduledAt: string;
}) => (
  <Button css={{ width: '100%', marginTop: themeVars.pad[12] }} onClick={() => setOpen(false)}>
    {`Schedule for ${scheduledAt}`}
  </Button>
);

export const OrderScheduleModal = observer(
  ({
    order,
    setOpen,
    open,
    isDesktop,
  }: {
    order: Order;
    setOpen: (open: boolean) => void;
    open: boolean;
    isDesktop: boolean;
  }) => {
    const checkout = useCheckoutContext();
    const [selectedDate, setSelectedDate] = useState<DateTime | null>(null);

    const scheduledAt = order.scheduledAt;
    const scheduledAtMillis = scheduledAt?.toMillis();
    const minimumScheduledAt = checkout?.minimumScheduledAt ?? null;
    const minimumScheduledAtMillis = minimumScheduledAt?.toMillis();
    const firstAvailableSlotStart =
      open && checkout && minimumScheduledAt
        ? (checkout.restaurant.hours.nextScheduleWindow(minimumScheduledAt)?.startTime ?? null)
        : null;
    const firstAvailableSlotStartMillis = firstAvailableSlotStart?.toMillis();
    const visibleDate = selectedDate ?? scheduledAt ?? firstAvailableSlotStart;
    const hasSelectedVisibleSlot =
      !!scheduledAt && !!visibleDate && scheduledAt.hasSame(visibleDate, 'day');
    const timeSlotOptions =
      open && checkout && visibleDate && minimumScheduledAt
        ? checkout.restaurant.hours.scheduleWindowsForDay(visibleDate, minimumScheduledAt)
        : [];

    // DateTime objects are recreated by getters; use millis dependencies to avoid effect churn.
    useEffect(() => {
      if (!open) return;
      setSelectedDate(scheduledAt ?? firstAvailableSlotStart);
    }, [firstAvailableSlotStartMillis, open, scheduledAtMillis]);

    const isDateDisabled: Matcher = useCallback(
      (day: Date) => {
        if (!checkout || !minimumScheduledAt) return true;
        const date = DateTime.fromJSDate(day).setZone(order.restaurant.timeZone, {
          keepLocalTime: true,
        });
        return !checkout.restaurant.hours.scheduleWindowsForDay(date, minimumScheduledAt).length;
      },
      [
        checkout?.restaurant.hours,
        checkout?.minimumScheduledLeadMinutes,
        minimumScheduledAtMillis,
        order.fulfillment,
        order.restaurant.timeZone,
      ],
    );

    if (!checkout) return null;

    return (
      <ModalNext
        withCloseButton
        opened={open}
        onRequestClose={() => setOpen(false)}
        className={orderScheduleModal}
      >
        {visibleDate ? (
          <div className={orderScheduleModalContent} data-id="order-schedule-modal">
            <div className={orderScheduleCalendar}>
              <DatePicker
                key={open ? visibleDate.toISODate() : 'closed'}
                defaultMonth={visibleDate.toJSDate()}
                mode="single"
                selected={
                  new Date(visibleDate.year, visibleDate.month - 1, visibleDate.day) // highlight the correct day, regardless of the user's timezone.
                }
                disabled={isDateDisabled}
                onSelect={(value) => {
                  if (!value) return;
                  setSelectedDate(
                    DateTime.fromJSDate(value).setZone(order.restaurant.timeZone, {
                      keepLocalTime: true,
                    }),
                  );
                }}
              />
              {isDesktop && scheduledAt && hasSelectedVisibleSlot && (
                <CloseButton
                  setOpen={setOpen}
                  scheduledAt={scheduledAt.toFormat('LLLL d, h:mm a')}
                />
              )}
            </div>
            <Stack className={orderScheduleTimesSection}>
              <Text variant="caption1">
                <TimeZoneName timeZone={order.restaurant.timeZone} />
              </Text>
              <div className={orderScheduleTimeSlotsScrollable}>
                <OrderTimeSlots
                  options={timeSlotOptions}
                  value={scheduledAt ?? undefined}
                  onChange={(value) => {
                    setSelectedDate(value);
                    order.scheduledAt = value;
                  }}
                />
              </div>
            </Stack>
            {!isDesktop && scheduledAt && hasSelectedVisibleSlot && (
              <CloseButton setOpen={setOpen} scheduledAt={scheduledAt.toFormat('LLLL d, h:mm a')} />
            )}
          </div>
        ) : (
          <div className={orderScheduleModalContent} data-id="order-schedule-modal">
            <Text variant="caption1">There are no available scheduled order times.</Text>
          </div>
        )}
      </ModalNext>
    );
  },
);
